package com.vamvisions.main.core.presentation.markdown

import org.intellij.markdown.IElementType
import org.intellij.markdown.MarkdownElementTypes
import org.intellij.markdown.MarkdownTokenTypes
import org.intellij.markdown.ast.ASTNode
import org.intellij.markdown.ast.getTextInNode
import org.intellij.markdown.flavours.gfm.GFMElementTypes
import org.intellij.markdown.flavours.gfm.GFMFlavourDescriptor
import org.intellij.markdown.flavours.gfm.GFMTokenTypes
import org.intellij.markdown.parser.MarkdownParser as IntellijMarkdownParser

/**
 * Turns document markdown (CommonMark with GFM tables and autolinks) into a sanitised [MdNode] tree.
 * Unsupported constructs, raw HTML included, become plain text with their literal source. Bare email
 * addresses become `mailto` links.
 */
object MarkdownParser {

    private val flavour = GFMFlavourDescriptor()
    private val emailPattern = Regex("""[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+""")

    fun parse(markdown: String): List<MdNode> {
        val root = IntellijMarkdownParser(flavour).buildMarkdownTreeFromString(markdown)
        return Builder(markdown).blocks(root.children)
    }

    private class Builder(private val source: String) {

        private fun ASTNode.text(): String = getTextInNode(source).toString()

        fun blocks(nodes: List<ASTNode>): List<MdNode> = nodes.flatMap(::block)

        private fun block(node: ASTNode): List<MdNode> = when (node.type) {
            MarkdownTokenTypes.EOL, MarkdownTokenTypes.WHITE_SPACE -> emptyList()
            MarkdownElementTypes.ATX_1 -> heading(1, node)
            MarkdownElementTypes.ATX_2 -> heading(2, node)
            MarkdownElementTypes.ATX_3 -> heading(3, node)
            MarkdownElementTypes.ATX_4, MarkdownElementTypes.ATX_5, MarkdownElementTypes.ATX_6 -> heading(4, node)
            MarkdownElementTypes.SETEXT_1 -> heading(1, node)
            MarkdownElementTypes.SETEXT_2 -> heading(2, node)
            MarkdownElementTypes.PARAGRAPH -> paragraph(node.children)
            MarkdownElementTypes.UNORDERED_LIST -> listOf(MdNode.BulletList(listItems(node)))
            MarkdownElementTypes.ORDERED_LIST -> listOf(MdNode.OrderedList(orderedListStart(node), listItems(node)))
            MarkdownTokenTypes.HORIZONTAL_RULE -> listOf(MdNode.Rule)
            GFMElementTypes.TABLE -> listOf(table(node))
            // Anything else (HTML blocks, code blocks, quotes, link definitions) is shown as its source.
            else -> listOfNotNull(literalParagraph(node.text()))
        }

        private fun heading(level: Int, node: ASTNode): List<MdNode> {
            val content = node.children.firstOrNull {
                it.type == MarkdownTokenTypes.ATX_CONTENT || it.type == MarkdownTokenTypes.SETEXT_CONTENT
            } ?: return emptyList()
            val inlines = normalize(inlines(content.children))
            return if (inlines.isEmpty()) emptyList() else listOf(MdNode.Heading(level, inlines))
        }

        /** CommonMark treats images as inline; here each image splits its paragraph into blocks. */
        private fun paragraph(children: List<ASTNode>): List<MdNode> {
            val result = mutableListOf<MdNode>()
            val pending = mutableListOf<ASTNode>()
            fun flush() {
                val inlines = normalize(inlines(pending))
                if (inlines.isNotEmpty()) result += MdNode.Paragraph(inlines)
                pending.clear()
            }
            children.forEach { child ->
                if (child.type == MarkdownElementTypes.IMAGE) {
                    flush()
                    image(child)?.let(result::add)
                } else {
                    pending += child
                }
            }
            flush()
            return result
        }

        private fun listItems(list: ASTNode): List<List<MdNode>> =
            list.children.filter { it.type == MarkdownElementTypes.LIST_ITEM }.map { item ->
                blocks(item.children.filterNot {
                    it.type == MarkdownTokenTypes.LIST_BULLET || it.type == MarkdownTokenTypes.LIST_NUMBER
                })
            }

        private fun orderedListStart(list: ASTNode): Int =
            list.children.firstOrNull { it.type == MarkdownElementTypes.LIST_ITEM }
                ?.children?.firstOrNull { it.type == MarkdownTokenTypes.LIST_NUMBER }
                ?.text()?.trim()?.trimEnd('.', ')')?.toIntOrNull() ?: 1

        private fun table(node: ASTNode): MdNode.Table {
            fun cells(row: ASTNode) = row.children
                .filter { it.type == GFMTokenTypes.CELL }
                .map { TableCell(normalize(inlines(it.children))) }
            val header = node.children.firstOrNull { it.type == GFMElementTypes.HEADER }?.let(::cells).orEmpty()
            val rows = node.children.filter { it.type == GFMElementTypes.ROW }.map(::cells)
            return MdNode.Table(header, rows)
        }

        private fun image(node: ASTNode): MdNode.Image? {
            val link = node.children.firstOrNull { it.type == MarkdownElementTypes.INLINE_LINK } ?: return null
            val alt = link.children.firstOrNull { it.type == MarkdownElementTypes.LINK_TEXT }
                ?.let { plainText(normalize(inlines(it.children.withoutBrackets()))) }
                .orEmpty()
            val src = destination(link)?.let(LinkPolicy::sanitizeImage) ?: return null
            return MdNode.Image(src, alt)
        }

        private fun literalParagraph(text: String): MdNode? =
            text.trim().takeIf { it.isNotEmpty() }?.let { MdNode.Paragraph(listOf(MdInline.Text(it))) }

        // Inlines

        private fun inlines(nodes: List<ASTNode>, insideLink: Boolean = false): List<MdInline> =
            nodes.flatMap { inline(it, insideLink) }

        private fun inline(node: ASTNode, insideLink: Boolean): List<MdInline> = when (node.type) {
            MarkdownTokenTypes.EOL, MarkdownTokenTypes.WHITE_SPACE -> listOf(MdInline.Text(" "))
            MarkdownTokenTypes.HARD_LINE_BREAK -> listOf(MdInline.LineBreak)
            MarkdownElementTypes.STRONG -> listOf(MdInline.Strong(inlines(node.children.withoutDelimiters(), insideLink)))
            MarkdownElementTypes.EMPH -> listOf(MdInline.Emphasis(inlines(node.children.withoutDelimiters(), insideLink)))
            MarkdownElementTypes.CODE_SPAN -> listOf(MdInline.Code(codeText(node)))
            MarkdownElementTypes.INLINE_LINK -> inlineLink(node, insideLink)
            MarkdownElementTypes.AUTOLINK -> autolink(node.text().removeSurrounding("<", ">"), insideLink)
            GFMTokenTypes.GFM_AUTOLINK -> autolink(node.text(), insideLink)
            MarkdownElementTypes.IMAGE -> listOf(MdInline.Text(node.text()))
            // Reference links have no definitions in these documents, raw HTML is never markup, and
            // any other composite keeps its literal source.
            else -> if (node.children.isEmpty() || node.type in literalComposites) {
                listOf(MdInline.Text(node.text()))
            } else {
                inlines(node.children, insideLink)
            }
        }

        private fun inlineLink(node: ASTNode, insideLink: Boolean): List<MdInline> {
            val label = node.children.firstOrNull { it.type == MarkdownElementTypes.LINK_TEXT }
                ?.let { inlines(it.children.withoutBrackets(), insideLink = true) }
                .orEmpty()
            val href = destination(node)?.let(LinkPolicy::sanitizeLink)
            return if (href == null || insideLink) label else listOf(MdInline.Link(href, label))
        }

        private fun autolink(url: String, insideLink: Boolean): List<MdInline> {
            val target = when {
                url.startsWith("www.", ignoreCase = true) -> "https://$url"
                '@' in url && ':' !in url -> "mailto:$url"
                else -> url
            }
            val href = LinkPolicy.sanitizeLink(target)
            return if (href == null || insideLink) listOf(MdInline.Text(url)) else listOf(MdInline.Link(href, listOf(MdInline.Text(url))))
        }

        private fun destination(link: ASTNode): String? =
            link.children.firstOrNull { it.type == MarkdownElementTypes.LINK_DESTINATION }
                ?.text()?.trim()?.removeSurrounding("<", ">")

        private fun codeText(node: ASTNode): String {
            val text = node.children.filterNot { it.type == MarkdownTokenTypes.BACKTICK }.joinToString("") { it.text() }
            return if (text.length > 2 && text.startsWith(" ") && text.endsWith(" ")) text.substring(1, text.length - 1) else text
        }

        private fun List<ASTNode>.withoutDelimiters() =
            filterNot { it.children.isEmpty() && (it.type == MarkdownTokenTypes.EMPH || it.type == GFMTokenTypes.TILDE) }

        private fun List<ASTNode>.withoutBrackets() =
            filterNot { it.type == MarkdownTokenTypes.LBRACKET || it.type == MarkdownTokenTypes.RBRACKET }

        private val literalComposites: Set<IElementType> = setOf(
            MarkdownElementTypes.SHORT_REFERENCE_LINK,
            MarkdownElementTypes.FULL_REFERENCE_LINK,
        )
    }

    // Normalisation

    /** Merges adjacent text, collapses whitespace, trims the block edges and links bare emails. */
    private fun normalize(inlines: List<MdInline>): List<MdInline> {
        val collapsed = collapse(inlines)
        return linkEmails(trimEdges(collapsed))
    }

    private fun collapse(inlines: List<MdInline>): List<MdInline> {
        val result = mutableListOf<MdInline>()
        inlines.forEach { inline ->
            val item = when (inline) {
                is MdInline.Text -> MdInline.Text(inline.value.replace(Regex("\\s+"), " "))
                is MdInline.Strong -> MdInline.Strong(collapse(inline.children))
                is MdInline.Emphasis -> MdInline.Emphasis(collapse(inline.children))
                is MdInline.Link -> MdInline.Link(inline.href, collapse(inline.children))
                else -> inline
            }
            val last = result.lastOrNull()
            if (item is MdInline.Text && last is MdInline.Text) {
                result[result.lastIndex] = MdInline.Text((last.value + item.value).replace(Regex(" {2,}"), " "))
            } else {
                result += item
            }
        }
        return result.filterNot { it is MdInline.Text && it.value.isEmpty() }
    }

    /** Removes spaces at the start and end of the block and on either side of a line break. */
    private fun trimEdges(inlines: List<MdInline>): List<MdInline> {
        val result = inlines.toMutableList()
        for (i in result.indices) {
            val item = result[i] as? MdInline.Text ?: continue
            var value = item.value
            if (i == 0 || result[i - 1] == MdInline.LineBreak) value = value.trimStart()
            if (i == result.lastIndex || result[i + 1] == MdInline.LineBreak) value = value.trimEnd()
            result[i] = MdInline.Text(value)
        }
        while (result.lastOrNull() == MdInline.LineBreak) result.removeAt(result.lastIndex)
        return result.filterNot { it is MdInline.Text && it.value.isEmpty() }
    }

    private fun linkEmails(inlines: List<MdInline>): List<MdInline> = inlines.flatMap { inline ->
        when (inline) {
            is MdInline.Text -> splitEmails(inline.value)
            is MdInline.Strong -> listOf(MdInline.Strong(linkEmails(inline.children)))
            is MdInline.Emphasis -> listOf(MdInline.Emphasis(linkEmails(inline.children)))
            else -> listOf(inline) // never inside links or code
        }
    }

    private fun splitEmails(text: String): List<MdInline> {
        val result = mutableListOf<MdInline>()
        var index = 0
        emailPattern.findAll(text).forEach { match ->
            if (match.range.first > index) result += MdInline.Text(text.substring(index, match.range.first))
            result += MdInline.Link("mailto:${match.value}", listOf(MdInline.Text(match.value)))
            index = match.range.last + 1
        }
        if (index < text.length) result += MdInline.Text(text.substring(index))
        return result
    }

    private fun plainText(inlines: List<MdInline>): String = inlines.joinToString("") {
        when (it) {
            is MdInline.Text -> it.value
            is MdInline.Code -> it.value
            is MdInline.Strong -> plainText(it.children)
            is MdInline.Emphasis -> plainText(it.children)
            is MdInline.Link -> plainText(it.children)
            MdInline.LineBreak -> " "
        }
    }
}
